import { BrowserNet } from '../utils/BrowserNet'
import { BrowserFileUtils } from '../utils/BrowserFileUtils'
import { API_ENDPOINTS } from '@/config/apiEndpoints'
import { BaseWebUrl } from '@/utils/request/baseWebUrl'
import {
  assertUploadRetSuccess,
  assertUploadSubmitSuccess,
  buildInitUploadParams,
  buildQueueInfoParams,
  buildSignedUploadQuery,
  buildUploadTaskParams,
  isPreCheckFastHit,
  parseQueueInfoResponse,
  parseUploadInitResponse,
  parseUploadProtocolResponse,
} from '@/platform/upload/uploadProtocol'

const uploadEndpoints = API_ENDPOINTS.upload
const websiteEndpoints = API_ENDPOINTS.website
const webUrl = new BaseWebUrl()

function isMapBinUpload(fileName: string): boolean {
  return fileName.toLowerCase().endsWith('map.bin')
}

async function getServerTimestamp(): Promise<number> {
  try {
    const response = await fetch(`${webUrl.baseWebsite}${websiteEndpoints.serverTime.replace(/^\/+/g, '')}?action=get_server_time`, {
      method: 'GET',
      credentials: 'include',
    })
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }
    const payload = await response.json() as {
      data?: {
        timestramp?: unknown
        timestamp?: unknown
      }
    }
    const value = Number(payload.data?.timestramp ?? payload.data?.timestamp ?? 0)
    if (Number.isFinite(value) && value > 0) return Math.floor(value)
  } catch {
  }
  return Math.floor(Date.now() / 1000)
}

/**
 * 上传配置
 */
export interface UploadConfig {
  guid: string
  version: string
  proj_ver: string
  sessionid: string
}

/**
 * 上传任务文件
 */
export interface UploadTaskFile {
  file: File
  fileName: string
  progressKey?: string
  op?: string      // 上传目标 0：网盘 1：素材库 2：暴走小f目录 3:[新]oweb
  shared?: string  // 是否是共享文件 0 代表是用户独享文件，1代表是用户共享文件
  contentMd5?: string
}

/**
 * 上传进度回调
 */
export type UploadProgressCallback = (
  fileName: string,
  percent: number,
  status: 'hashing' | 'uploading' | 'completed' | 'error'
) => void

export type UploadRequestLogCallback = (
  status: 'running' | 'done' | 'error',
  detail: string
) => void

export type BrowserFileUploadOptions = {
  skipPreCheck?: boolean
}

export type UploadFileResult = {
  code: 1
  msg: string
  data: string[]
}

/**
 * 浏览器兼容的文件上传类
 * 支持 PC/Pad/iPad 浏览器
 */
export class BrowserFileUpload {
  private getuploadConfigUrl = `${uploadEndpoints.initUpload}?`
  private preCheckUrl = `${uploadEndpoints.preCheck}?`
  private uploadUrl = ""
  private uploadOrigin = ""
  private getQueueInfoUrl = `${uploadEndpoints.getQueueInfo}?`

  public tranId = ""
  private uploadTaskFileList: UploadTaskFile[] = []
  private browserNet = BrowserNet.getInstance()
  private config: UploadConfig

  private onProgress?: UploadProgressCallback
  private onRequestLog?: UploadRequestLogCallback
  private md5PromiseCache = new WeakMap<File, Promise<string>>()
  private options: BrowserFileUploadOptions

  /**
   * 开发环境下把服务端返回的绝对上传地址改写到 Vite 上传代理，
   * 避免浏览器直接请求上传域名时触发跨域。
   */
  private normalizeUploadUrl(uploadUrl: string): string {
    try {
      const url = new URL(uploadUrl)
      url.protocol = 'https:'
      this.uploadOrigin = url.origin
      const normalizedUrl = import.meta.env.DEV ? `/__dev_proxy_upload${url.pathname}${url.search}` : url.toString()
      const detail = `upload_url · serverOrigin=${this.uploadOrigin} · request=${normalizedUrl}`
      this.logRequest('done', detail)
      return normalizedUrl
    } catch (error) {
      if (error instanceof Error) throw error
      throw new Error(`上传地址无效：${uploadUrl}`)
    }
  }

  private appendUploadProxyOrigin(url: string): string {
    if (!import.meta.env.DEV || !this.uploadOrigin) return url
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}__upload_origin=${encodeURIComponent(this.uploadOrigin)}`
  }

  /**
   * 上传文件
   * @param config 上传配置
   * @param fileList 文件列表 (使用浏览器 File 对象)
   * @param onProgress 进度回调
   */
  constructor(
    config: UploadConfig,
    fileList: UploadTaskFile[],
    onProgress?: UploadProgressCallback,
    onRequestLog?: UploadRequestLogCallback,
    options?: BrowserFileUploadOptions,
  ) {
    this.config = config
    this.uploadTaskFileList = fileList
    this.onProgress = onProgress
    this.onRequestLog = onRequestLog
    this.options = options ?? {}
  }

  private logRequest(status: 'running' | 'done' | 'error', detail: string) {
    this.onRequestLog?.(status, detail)
  }

  private getProgressKey(task: UploadTaskFile): string {
    return task.progressKey?.trim() || task.fileName
  }

  private async getContentMd5(task: UploadTaskFile, emitProgress: boolean): Promise<string> {
    if (task.contentMd5?.trim()) {
      if (emitProgress && this.onProgress) {
        this.onProgress(this.getProgressKey(task), 100, 'hashing')
      }
      return task.contentMd5.trim()
    }

    const cached = this.md5PromiseCache.get(task.file)
    if (cached) {
      const md5 = await cached
      task.contentMd5 = md5
      if (emitProgress && this.onProgress) {
        this.onProgress(this.getProgressKey(task), 100, 'hashing')
      }
      return md5
    }

    const promise = BrowserFileUtils.getMd5HashFromFile(task.file, (percent) => {
      if (emitProgress && this.onProgress) {
        this.onProgress(this.getProgressKey(task), percent, 'hashing')
      }
    })
    this.md5PromiseCache.set(task.file, promise)
    const md5 = await promise
    task.contentMd5 = md5
    return md5
  }

  /** 执行整批上传任务：初始化、预检查、上传并返回每个文件的结果。 */
  public async uploadFile(): Promise<UploadFileResult> {
    const configResponse = await this.getUploadConfig()
    const configRet = parseUploadInitResponse(configResponse)
    this.tranId = configRet.tranId
    this.uploadUrl = this.normalizeUploadUrl(configRet.uploadUrl)

    const uploadPromises = this.options.skipPreCheck
      ? this.uploadTaskFileList.map(task => this.readAndUploadFile(task))
      : await this.buildUploadPromises()

    this.logRequest('running', `upload batch · waiting · count=${uploadPromises.length}`)
    const uploadResponses = await Promise.all(uploadPromises)
    this.logRequest('done', `upload batch · resolved · count=${uploadResponses.length}`)
    return {
      code: 1,
      msg: 'task submit success',
      data: uploadResponses,
    }
  }

  private async buildUploadPromises(): Promise<Promise<string>[]> {
    const preCheckResponses = await Promise.all(this.uploadTaskFileList.map(task => this.preCheck(task)))
    return preCheckResponses.map((response, index) => {
      const task = this.uploadTaskFileList[index]!
      const ret = parseUploadProtocolResponse(task.fileName, response, '预检查')
      assertUploadRetSuccess(task.fileName, ret, '预检查', response)
      if (isPreCheckFastHit(ret)) {
        this.onProgress?.(this.getProgressKey(task), 100, 'completed')
        return Promise.resolve(JSON.stringify(ret))
      }
      return this.readAndUploadFile(task)
    })
  }

  /** 获取本次上传任务的临时配置与上传目标地址。 */
  private async getUploadConfig(): Promise<string> {
    // 对齐工具 3.0：initUpload 的 version 历史上实际传 guid，发布版本字段使用 project_ver。
    const params = buildInitUploadParams({
      version: this.config.guid,
      projectVersion: this.config.proj_ver,
      sessionId: this.config.sessionid,
      guid: this.config.guid,
    })
    const url = this.getuploadConfigUrl + buildSignedUploadQuery(params)

    this.logRequest('running', `GET ${uploadEndpoints.initUpload} · start`)
    try {
      const response = await this.browserNet.requestGet(url)
      this.logRequest('done', `GET ${uploadEndpoints.initUpload} · done`)
      return response
    } catch (error) {
      this.logRequest('error', `GET ${uploadEndpoints.initUpload} · ${error instanceof Error ? error.message : '请求失败'}`)
      throw error
    }
  }

  /** 对单个文件执行预检查，决定是否可以秒传。 */
  private async preCheck(task: UploadTaskFile): Promise<string> {
    // 通知进度：计算 MD5 中
    if (this.onProgress) {
      this.onProgress(this.getProgressKey(task), 0, 'hashing')
    }

    // 计算文件 MD5
    const contentMd5 = await this.getContentMd5(task, true)

    const params = buildUploadTaskParams({
      contentMd5,
      fileName: task.fileName,
      fileSize: task.file.size,
      guid: this.config.guid,
      op: task.op,
      shared: task.shared,
      projectVersion: this.config.proj_ver,
      sessionId: this.config.sessionid,
      tranId: this.tranId,
      version: this.config.version,
    })
    const url = this.preCheckUrl + buildSignedUploadQuery(params)

    const isMapBin = isMapBinUpload(task.fileName)
    this.logRequest('running', `GET ${uploadEndpoints.preCheck} · start · ${task.fileName}`)
    if (isMapBin) {
      this.logRequest(
        'running',
        `Map.bin preCheck · file=${task.fileName} · size=${task.file.size} · md5=${contentMd5} · op=${task.op ?? ''} · shared=${task.shared ?? ''}`,
      )
    }
    try {
      const response = await this.browserNet.requestGet(url)
      const ret = parseUploadProtocolResponse(task.fileName, response, '预检查')
      this.logRequest('done', `GET ${uploadEndpoints.preCheck} · done · ${task.fileName} · ret=${ret.ret ?? ''} code=${ret.code ?? ''}`.trim())
      if (isMapBin) {
        this.logRequest('done', `Map.bin preCheck response · ${response}`)
      }
      return response
    } catch (error) {
      this.logRequest('error', `GET ${uploadEndpoints.preCheck} · ${task.fileName} · ${error instanceof Error ? error.message : '请求失败'}`)
      throw error
    }
  }

  /** 在预检查未命中秒传时，执行真实文件上传。 */
  private async readAndUploadFile(task: UploadTaskFile): Promise<string> {
    // 计算 MD5
    const contentMd5 = await this.getContentMd5(task, false)

    const params = buildUploadTaskParams({
      contentMd5,
      fileName: task.fileName,
      fileSize: task.file.size,
      guid: this.config.guid,
      op: task.op,
      shared: task.shared,
      projectVersion: this.config.proj_ver,
      sessionId: this.config.sessionid,
      tranId: this.tranId,
      version: this.config.version,
    })
    let url = this.uploadUrl + (this.uploadUrl.includes('?') ? '&' : '?') + buildSignedUploadQuery(params)
    url = this.appendUploadProxyOrigin(url)

    // 使用带进度的上传
    const isMapBin = isMapBinUpload(task.fileName)
    this.logRequest('running', `POST upload_url · start · ${task.fileName}`)
    if (isMapBin) {
      this.logRequest(
        'running',
        `Map.bin upload · origin=${this.uploadOrigin} · file=${task.fileName} · size=${task.file.size} · md5=${contentMd5}`,
      )
    }
    return this.browserNet.uploadWithProgress(
      task.file,
      task.fileName,
      url,
      (percent) => {
        if (this.onProgress) {
          this.onProgress(this.getProgressKey(task), percent, 'uploading')
        }
      },
      (status, detail) => {
        this.logRequest(status === 'load' ? 'running' : 'error', `POST upload_url · ${status} · ${detail}`)
      },
    ).then(response => {
      this.logRequest('running', `POST upload_url · response received · ${task.fileName}`)
      assertUploadSubmitSuccess(task.fileName, response)
      if (this.onProgress) {
        this.onProgress(this.getProgressKey(task), 100, 'completed')
      }
      this.logRequest('done', `POST upload_url · done · ${task.fileName}`)
      this.logRequest('done', `POST upload_url · assert ok · ${task.fileName}`)
      if (isMapBin) {
        this.logRequest('done', `Map.bin upload response · ${response}`)
      }
      return response
    }).catch(error => {
      if (this.onProgress) {
        this.onProgress(this.getProgressKey(task), 0, 'error')
      }
      this.logRequest('error', `POST upload_url · ${task.fileName} · ${error instanceof Error ? error.message : '上传失败'}`)
      throw error
    })
  }

  /** 请求当前 tranId 对应的队列信息。 */
  public async getQueueInfo(): Promise<string> {
    const timestamp = await getServerTimestamp()
    const params = buildQueueInfoParams({
      tranId: this.tranId,
      sessionId: this.config.sessionid,
      guid: this.config.guid,
      version: this.config.version,
      count: this.uploadTaskFileList.length,
      timestamp,
    })
    const url = this.getQueueInfoUrl + buildSignedUploadQuery(params)

    this.logRequest('running', `GET ${uploadEndpoints.getQueueInfo} · start`)
    try {
      const response = await this.browserNet.requestGet(url)
      const ret = parseQueueInfoResponse(response)
      this.logRequest('done', `GET ${uploadEndpoints.getQueueInfo} · done · queue=${ret.queueCount}`)
      return response
    } catch (error) {
      this.logRequest('error', `GET ${uploadEndpoints.getQueueInfo} · ${error instanceof Error ? error.message : '请求失败'}`)
      throw error
    }
  }

}

export default BrowserFileUpload
